/**
 * Adapter for trader profile APIs that maintains backward compatibility with legacy mesa-defined trader profile interfaces.
 * `mesa`: https://github.com/moment-technology/mesa (legacy backend service for trader profiles - `/trade-desk/` routes)
 * `orders-api: https://github.com/moment-technology/orders-api (new backend service for trader profiles - `/trading/` routes)
 *
 * `mesa`-defined interfaces are used directly in many places within the `web-ui` application.
 *
 * The adapter:
 * - Adheres to the legacy mesa interface (from the perspective of the FE)
 * - Automatically enriches requests with orgId, parentOrgId from the current user's session (required for new API)
 * - Transforms data from legacy mesa schema to new orders-api schema when making requests
 * - Transforms data from new orders-api schema to legacy mesa schema to preserve existing FE interface
 *
 * NOTE:
 * Before we fully remove mesa service and its OpenAPI schema, we will need a solution to preserve this on the FE
 * (unless we want to do some refactoring).
 */

import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import type {
  TypedUseMutation,
  TypedUseQuery,
} from '@reduxjs/toolkit/query/react'
import type {
  AddVenueTraderConfig_TradeDeskApiArg,
  CreateTraderProfile_TradeDeskApiArg,
  DeleteVenueTraderConfig_TradeDeskApiArg,
  GetTraderProfile_TradeDeskApiArg,
  TraderProfile as LegacyTraderProfile,
  VenueTraderConfig as LegacyVenueTraderConfig,
  UpdateVenueTraderConfig_TradeDeskApiArg,
  UpsertBloombergId_TradeDeskApiArg,
} from '@/types/api/legacy-trader-profile'
import type {
  CreateTraderProfileApiArg,
  GetTraderProfileApiArg,
  TraderProfile as NewTraderProfile,
  UpdateTraderProfileApiArg,
  VenueTraderInfo,
} from './orders-slice.gen'

import { toVenueTraderInfo } from '@/utils/flatten-venue-trader-configs'

import {
  useCreateTraderProfileMutation,
  useGetDefaultTraderForOrgQuery,
  useGetTraderProfileQuery,
  useUpdateTraderProfileMutation,

  // Unused by existing UI
  // useDeleteDefaultTraderForOrgMutation,
  // useDeleteTraderProfileMutation,
  // usePostDefaultTraderForOrgMutation
} from './trader-profiles-slice'
import { useGetSelfQuery } from './user-slice'

// =============================================================================
// Base Query Type (matches base-api-slice.ts)
// =============================================================================

type AppBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
>

/**
 * Adapted hooks need a superset of the original params. Anything using
 * the adapters need to conditionally enrich with:
 * - orgId - new param needed for new API
 * - userId - new param needed for new API (creation previously did not require)
 * - existingTraderProfile - new API requires updates to provide the entire blob, while old
 *   was more atomic
 *
 * This hook centralizes the enrichment required for the new API.
 */
type EnrichArgs = {
  isError: boolean
  orgId?: string
  userId?: string
  existingTraderProfile?: NewTraderProfile
}

// Centralized enrichment for new API required params
function useEnrichArgs({
  skipTraderProfile,
}: {
  skipTraderProfile: boolean
}): EnrichArgs {
  const { data: self, isError: isSelfError } = useGetSelfQuery()

  const { data: existingTraderProfile, isError: isProfileError } =
    useGetTraderProfileQuery(
      { userId: self?.id || '', orgId: self?.organization_id || '' },
      { skip: isSelfError || skipTraderProfile },
    )

  return {
    isError: isSelfError || isProfileError,
    // New API requires us to supply org
    orgId: self?.organization_id,

    userId: self?.id,
    // New API requires you to update the entire blob, old API allowed updating specific venue
    existingTraderProfile,
  }
}

// =============================================================================
// Higher-order function for creating adapted query hooks
// =============================================================================

type QueryOptions = { skip?: boolean }

/**
 * Creates an adapted query hook that wraps the orders-api interface.
 *
 * @param useNewHook - The new RTK Query hook
 * @param transformArgs - Function to transform legacy args to new args (receives EnrichArgs)
 * @param transformResult - Function to transform new API result to legacy shape
 */
function createAdaptedQueryHook<LegacyArgs, LegacyResult, NewArgs, NewResult>(
  useNewHook: TypedUseQuery<NewResult, NewArgs, AppBaseQuery>,
  transformArgs: ArgsTransformer<LegacyArgs, NewArgs>,
  transformResult: (result: NewResult) => LegacyResult,
) {
  return function useAdaptedQuery(
    legacyArgs: LegacyArgs,
    options?: QueryOptions,
  ) {
    const enrichArgs = useEnrichArgs({
      skipTraderProfile: true,
    })
    const { orgId, isError } = enrichArgs

    const args = orgId
      ? transformArgs(legacyArgs, enrichArgs)
      : (null as unknown as NewArgs)
    const newResult = useNewHook(args, {
      ...options,
      skip: options?.skip || !orgId || isError,
    })

    const transformedResult = {
      ...newResult,
      isError: newResult.isError || isError,
      data: newResult.data ? transformResult(newResult.data) : undefined,
    }
    return transformedResult
  }
}

/**
 * Transforms legacy args to new args using enriched context.
 * Used by both query and mutation adapters for consistency.
 */
type ArgsTransformer<LegacyArgs, NewArgs> = (
  legacyArgs: LegacyArgs,
  enrichArgs: EnrichArgs,
) => NewArgs

// =============================================================================
// Higher-order function for creating adapted mutation hooks
// =============================================================================

/**
 * Creates an adapted mutation hook that wraps the orders-api interface.
 *
 * Uses RTK Query's TypedUseMutation for proper typing of both legacy and new hooks.
 *
 * @param useLegacyHook - The legacy RTK Query mutation hook
 * @param useNewHook - The new RTK Query mutation hook
 * @param transformArgs - Function to transform legacy args to new args (receives orgId)
 * @param transformResult - Function to transform new API result to legacy shape
 */
function createAdaptedMutationHook<
  LegacyArgs,
  LegacyResult,
  NewArgs,
  NewResult,
>(
  useNewHook: TypedUseMutation<NewResult, NewArgs, AppBaseQuery>,
  transformArgs: ArgsTransformer<LegacyArgs, NewArgs>,
  transformResult: (result: NewResult) => LegacyResult,
  { skipTraderProfile }: { skipTraderProfile: boolean } = {
    skipTraderProfile: true,
  },
): TypedUseMutation<LegacyResult, LegacyArgs, AppBaseQuery> {
  return function useAdaptedMutation() {
    const enrichArgs = useEnrichArgs({
      skipTraderProfile,
    })

    const { isError } = enrichArgs
    const [newTrigger, newState] = useNewHook()

    // Wrap the new trigger to transform args and results
    const wrappedTrigger = async (legacyArgs: LegacyArgs) => {
      if (isError) {
        throw new Error(
          'Failed to enrich dependencies for trader profile mutation',
        )
      }

      const transformedArgs = transformArgs(legacyArgs, enrichArgs)

      const result = await newTrigger(transformedArgs)
      if (!result.data) return result
      return {
        ...result,
        data: transformResult(result.data),
      }
    }

    // Transform the state data if present
    const transformedState = {
      ...newState,
      data: newState.data ? transformResult(newState.data) : undefined,
    }

    return [wrappedTrigger, transformedState] as unknown as ReturnType<
      TypedUseMutation<LegacyResult, LegacyArgs, AppBaseQuery>
    >
  } as TypedUseMutation<LegacyResult, LegacyArgs, AppBaseQuery>
}

// =============================================================================
// Arg Transformers - (Legacy -> New API format)
// =============================================================================

const transformGetTraderProfileArgs: ArgsTransformer<
  GetTraderProfile_TradeDeskApiArg,
  GetTraderProfileApiArg
> = (args, { orgId }) => {
  if (!orgId) {
    throw new Error('orgId is required for getTraderProfile')
  }
  return {
    orgId,
    userId: args.userId,
  }
}

const transformLegacyVenueConfigs = (
  legacyConfigs: Record<string, LegacyVenueTraderConfig>,
): VenueTraderInfo[] => {
  const venueTraderInfo: VenueTraderInfo[] = []

  // Convert venue_trader_configs map to venue_trader_info array
  for (const [venueMpid, config] of Object.entries(legacyConfigs)) {
    venueTraderInfo.push(toVenueTraderInfo(venueMpid, config.venue_trader_id))
  }

  return venueTraderInfo
}

const transformCreateTraderProfileArgs: ArgsTransformer<
  CreateTraderProfile_TradeDeskApiArg,
  CreateTraderProfileApiArg
> = (args, { orgId, userId }) => {
  if (!orgId) {
    throw new Error('orgId is required for createTraderProfile')
  }
  if (!userId) {
    throw new Error('userId is required for createTraderProfile')
  }

  const venueTraderInfo = transformLegacyVenueConfigs(
    args.createTraderProfileRequest.venue_trader_configs,
  )

  return {
    orgId,
    userId,
    createTraderProfileRequest: {
      venue_trader_info: venueTraderInfo,
    },
  }
}

const transformAddVenueTraderConfigArgs: ArgsTransformer<
  AddVenueTraderConfig_TradeDeskApiArg,
  UpdateTraderProfileApiArg
> = (args, { orgId, existingTraderProfile }) => {
  if (!orgId) {
    throw new Error('orgId is required for addVenueTraderConfig')
  }

  const { userId, venueMpid, addVenueTraderConfigRequest } = args
  const { venue_trader_id } = addVenueTraderConfigRequest

  // Start with existing venue_trader_info or empty array
  const existingInfo = existingTraderProfile?.venue_trader_info || []

  return {
    orgId,
    userId,
    updateTraderProfileRequest: {
      bloomberg_id: existingTraderProfile?.bloomberg_id,
      venue_trader_info: [
        ...existingInfo,
        toVenueTraderInfo(venueMpid, venue_trader_id),
      ],
    },
  }
}

const transformUpdateVenueTraderConfigArgs: ArgsTransformer<
  UpdateVenueTraderConfig_TradeDeskApiArg,
  UpdateTraderProfileApiArg
> = (args, { orgId, existingTraderProfile }) => {
  if (!orgId) {
    throw new Error('orgId is required for updateVenueTraderConfig')
  }

  const { userId, venueMpid, updateVenueTraderConfigRequest } = args
  const { venue_trader_id } = updateVenueTraderConfigRequest

  // Filter out existing entries for this venue (we're replacing them)
  const existingInfo = existingTraderProfile?.venue_trader_info || []
  const otherVenueInfo = existingInfo.filter(
    (info) => info.venue_mpid !== venueMpid,
  )

  return {
    orgId,
    userId,
    updateTraderProfileRequest: {
      bloomberg_id: existingTraderProfile?.bloomberg_id,
      venue_trader_info: [
        ...otherVenueInfo,
        toVenueTraderInfo(venueMpid, venue_trader_id),
      ],
    },
  }
}

const transformNewToLegacyTraderProfile = (
  newProfile: NewTraderProfile,
): LegacyTraderProfile => {
  const venueTraderConfigs: { [key: string]: LegacyVenueTraderConfig } = {}

  // Older profiles can hold several entries per venue (one per custodian).
  // The backend uses the first entry for a venue, so the UI shows that one.
  for (const { venue_mpid, venue_trader_id } of newProfile.venue_trader_info) {
    if (!venueTraderConfigs[venue_mpid]) {
      venueTraderConfigs[venue_mpid] = { venue_trader_id }
    }
  }

  return {
    user_id: newProfile.user_id,
    bloomberg_id: newProfile.bloomberg_id,
    venue_trader_configs: venueTraderConfigs,
  }
}

const transformDeleteVenueTraderConfigArgs: ArgsTransformer<
  DeleteVenueTraderConfig_TradeDeskApiArg,
  UpdateTraderProfileApiArg
> = (args, { orgId, existingTraderProfile }) => {
  if (!orgId) {
    throw new Error('orgId is required for deleteVenueTraderConfig')
  }

  const { userId, venueMpid } = args

  // Filter out the venue being deleted from existing venue_trader_info
  const existingInfo = existingTraderProfile?.venue_trader_info || []
  const filteredInfo = existingInfo.filter(
    (info) => info.venue_mpid !== venueMpid,
  )

  return {
    orgId,
    userId,
    updateTraderProfileRequest: {
      bloomberg_id: existingTraderProfile?.bloomberg_id,
      venue_trader_info: filteredInfo,
    },
  }
}

const transformUpsertBloombergIdArgs: ArgsTransformer<
  UpsertBloombergId_TradeDeskApiArg,
  UpdateTraderProfileApiArg
> = (args, { orgId, existingTraderProfile }) => {
  if (!orgId) {
    throw new Error('orgId is required for upsertBloombergId')
  }

  const { userId, upsertBloombergIdRequest } = args

  return {
    orgId,
    userId,
    updateTraderProfileRequest: {
      bloomberg_id: upsertBloombergIdRequest.bloomberg_id,
      venue_trader_info: existingTraderProfile?.venue_trader_info || [],
    },
  }
}

// =============================================================================
// Result Transformers - (New API -> Legacy format)
// =============================================================================

const transformGetTraderProfileResult = transformNewToLegacyTraderProfile
const transformCreateTraderProfileResult = transformNewToLegacyTraderProfile
const transformAddVenueTraderConfigResult = transformNewToLegacyTraderProfile
const transformDeleteVenueTraderConfigResult = transformNewToLegacyTraderProfile
const transformUpsertBloombergIdResult = transformNewToLegacyTraderProfile

// =============================================================================
// Adapted Hooks - Query Hooks
// =============================================================================

/**
 * Custom adapted hook for GetDefaultTraderProfileForParentOrg.
 *
 * The new API doesn't have parity - it only returns a user_id, not the full profile.
 * To get the full "default trader profile", we chain two calls:
 * 1. GetDefaultTraderForOrg -> returns { user_id }
 * 2. GetTraderProfile(user_id) -> returns the full TraderProfile
 */
export function useAdaptedGetDefaultTraderProfileForParentOrgQuery(
  // biome-ignore lint/suspicious/noConfusingVoidType: Bulk suppression as part of biome migration
  _args: void,
  options?: QueryOptions,
) {
  const { data: self, isError: isSelfError } = useGetSelfQuery()

  const parentOrgID = self?.parent_organization.id

  // New API path: Step 1 - Get default trader user ID for parent org
  const defaultTraderResult = useGetDefaultTraderForOrgQuery(
    { orgId: parentOrgID || '' },
    {
      ...options,
      skip: options?.skip || !parentOrgID || isSelfError,
    },
  )

  // New API path: Step 2 - Get the full trader profile for the default user
  const defaultUserId = defaultTraderResult.data?.user_id
  const traderProfileResult = useGetTraderProfileQuery(
    { orgId: parentOrgID || '', userId: defaultUserId || '' },
    {
      ...options,
      skip:
        options?.skip ||
        !parentOrgID ||
        !defaultUserId ||
        defaultTraderResult.isError,
    },
  )

  // Combine loading states from both chained calls
  const isLoading =
    defaultTraderResult.isLoading || traderProfileResult.isLoading
  const isFetching =
    defaultTraderResult.isFetching || traderProfileResult.isFetching
  const isError =
    isSelfError || defaultTraderResult.isError || traderProfileResult.isError

  return {
    ...traderProfileResult,
    isLoading,
    isFetching,
    isError,
    data: traderProfileResult.data
      ? transformNewToLegacyTraderProfile(traderProfileResult.data)
      : undefined,
  }
}

export const useAdaptedGetTraderProfileQuery = createAdaptedQueryHook(
  useGetTraderProfileQuery,
  transformGetTraderProfileArgs,
  transformGetTraderProfileResult,
)

// =============================================================================
// Adapted Hooks - Mutation Hooks
// =============================================================================

export const useAdaptedCreateTraderProfileMutation = createAdaptedMutationHook(
  useCreateTraderProfileMutation,
  transformCreateTraderProfileArgs,
  transformCreateTraderProfileResult,
)
export const useAdaptedAddVenueTraderConfigMutation = createAdaptedMutationHook(
  useUpdateTraderProfileMutation,
  transformAddVenueTraderConfigArgs,
  transformAddVenueTraderConfigResult,
  { skipTraderProfile: false }, // need to pass whole blob by enriching with existing trader profile
)
export const useAdaptedUpdateVenueTraderConfigMutation =
  createAdaptedMutationHook(
    useUpdateTraderProfileMutation,
    transformUpdateVenueTraderConfigArgs,
    transformNewToLegacyTraderProfile,
    { skipTraderProfile: false }, // need to pass whole blob by enriching with existing trader profile
  )
export const useAdaptedDeleteVenueTraderConfigMutation =
  createAdaptedMutationHook(
    useUpdateTraderProfileMutation,
    transformDeleteVenueTraderConfigArgs,
    transformDeleteVenueTraderConfigResult,
    { skipTraderProfile: false }, // need to pass whole blob by enriching with existing trader profile
  )
export const useAdaptedUpsertBloombergIdMutation = createAdaptedMutationHook(
  useUpdateTraderProfileMutation,
  transformUpsertBloombergIdArgs,
  transformUpsertBloombergIdResult,
  { skipTraderProfile: false }, // need to pass whole blob by enriching with existing trader profile
)
